import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { createCheckoutSession, createPortalSession, getBillingStatus } from '../api/api';

const Billing = () => {
    const { user, fetchUser } = useContext(AuthContext);
    const [searchParams] = useSearchParams();
    const [billing, setBilling] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const refreshBilling = async () => {
        try {
            const response = await getBillingStatus();
            setBilling(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Δεν ήταν δυνατή η φόρτωση του billing status.');
        }
    };

    useEffect(() => {
        refreshBilling();
    }, []);

    useEffect(() => {
        if (searchParams.get('checkout') !== 'success') return;
        let cancelled = false;
        let attempts = 0;
        const sync = async () => {
            attempts += 1;
            await fetchUser();
            try {
                const response = await getBillingStatus();
                if (cancelled) return;
                setBilling(response.data);
                if (response.data.scanner_access || attempts >= 10) return;
            } catch (_) {
                if (attempts >= 10) return;
            }
            window.setTimeout(sync, 1500);
        };
        const timer = window.setTimeout(sync, 500);
        return () => { cancelled = true; window.clearTimeout(timer); };
    }, [searchParams, fetchUser]);

    const handleCheckout = async () => {
        setError('');
        setLoading(true);
        try {
            const response = await createCheckoutSession();
            window.location.assign(response.data.url);
        } catch (err) {
            setError(err.response?.data?.detail || 'Δεν ήταν δυνατή η μετάβαση στο ασφαλές Stripe Checkout.');
            setLoading(false);
        }
    };

    const handleManageSubscription = async () => {
        setError('');
        setLoading(true);
        try {
            const response = await createPortalSession();
            window.location.assign(response.data.url);
        } catch (err) {
            setError(err.response?.data?.detail || 'Δεν ήταν δυνατή η φόρτωση του Stripe Customer Portal.');
            setLoading(false);
        }
    };

    if (!user) return null;

    const packageOwned = Boolean(billing?.scanner_package_owned ?? user.scanner_package_purchased_at);
    const privateBeta = Boolean(billing?.private_beta_access ?? user.private_beta_access);
    const scannerAccess = Boolean(billing?.scanner_access ?? user.scanner_access);
    const initialTrialDays = Number(billing?.initial_scanner_trial_days ?? 30);
    const subscriptionStatus = billing?.scanner_subscription_status || user.subscription_status || 'inactive';
    const billingConfigured = billing?.billing_configured !== false;
    const checkoutSucceeded = searchParams.get('checkout') === 'success';
    const checkoutCancelled = searchParams.get('checkout') === 'cancelled';

    return (
        <div style={{maxWidth: '920px', margin: '0 auto'}}>
            <h1>Πρόσβαση & Πληρωμές</h1>

            {checkoutSucceeded && (
                <div className="card" style={{borderColor: '#2f9e65'}}>
                    <strong>Η πληρωμή ολοκληρώθηκε στο Stripe.</strong>
                    <p style={{marginTop: '0.5rem'}}>Ενημερώνουμε τώρα την πρόσβασή σου από το επιβεβαιωμένο Stripe webhook.</p>
                </div>
            )}
            {checkoutCancelled && (
                <div className="card">
                    <strong>Το checkout ακυρώθηκε.</strong>
                    <p style={{marginTop: '0.5rem'}}>Δεν έγινε χρέωση. Μπορείς να συνεχίσεις όποτε θέλεις.</p>
                </div>
            )}
            {error && <div className="error-message" style={{marginBottom: '1rem'}}>{error}</div>}

            <div className="card">
                <div style={{display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap'}}>
                    <div>
                        <div style={{fontSize: '0.78rem', color: '#7aaeff', fontWeight: 700, letterSpacing: '.08em'}}>ALGOSTOCK SCANNER</div>
                        <h2 style={{marginTop: '0.35rem'}}>Scanner + Trading Framework</h2>
                    </div>
                    <div style={{textAlign: 'right'}}>
                        <div style={{fontWeight: 800, fontSize: '1.05rem'}}>
                            {privateBeta ? 'PRIVATE BETA · ΔΩΡΕΑΝ' : scannerAccess ? 'ΕΝΕΡΓΗ ΠΡΟΣΒΑΣΗ' : packageOwned ? 'PACKAGE OWNED' : 'ΔΕΝ ΕΧΕΙ ΑΓΟΡΑΣΤΕΙ'}
                        </div>
                        <div style={{color: '#a0a8b5', fontSize: '0.86rem'}}>{privateBeta ? 'Invite-only access' : `Subscription: ${String(subscriptionStatus).toUpperCase()}`}</div>
                    </div>
                </div>

                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: '1rem', marginTop: '1.5rem'}}>
                    <div style={{padding: '1rem', border: '1px solid rgba(255,255,255,.08)', borderRadius: '12px'}}>
                        <strong>Εφάπαξ αγορά · VAT included</strong>
                        <div style={{fontSize: '2rem', fontWeight: 800, marginTop: '.35rem'}}>€999</div>
                        <p style={{fontSize: '.9rem', color: '#a0a8b5'}}>Product ownership, AlgoStock Trading Framework και αρχικό onboarding.</p>
                    </div>
                    <div style={{padding: '1rem', border: '1px solid rgba(255,255,255,.08)', borderRadius: '12px'}}>
                        <strong>Scanner access · VAT included</strong>
                        <div style={{fontSize: '2rem', fontWeight: 800, marginTop: '.35rem'}}>€79 / μήνα</div>
                        <p style={{fontSize: '.9rem', color: '#a0a8b5'}}>Συνεχής πρόσβαση στο cloud Scanner. Στην αρχική αγορά οι πρώτες {initialTrialDays} ημέρες περιλαμβάνονται και η πρώτη χρέωση €79 γίνεται μετά.</p>
                    </div>
                </div>

                {!privateBeta && !packageOwned && (
                    <div style={{marginTop: '1.5rem'}}>
                        <p>Πληρώνεις €999 τώρα για το package. Το Scanner ενεργοποιείται αμέσως και η πρώτη χρέωση των €79 γίνεται μετά από {initialTrialDays} ημέρες.</p>
                        <button className="control-button" disabled={loading || !billingConfigured} onClick={handleCheckout}>
                            {loading ? 'Μετάβαση στο Stripe…' : `Αγορά & ενεργοποίηση — €999 τώρα · €79/μήνα μετά από ${initialTrialDays} ημέρες`}
                        </button>
                    </div>
                )}

                {!privateBeta && packageOwned && !scannerAccess && (
                    <div style={{marginTop: '1.5rem'}}>
                        <p>Το εφάπαξ package είναι ήδη δικό σου. Για επανενεργοποίηση πληρώνεις μόνο τη μηνιαία συνδρομή Scanner.</p>
                        <button className="control-button" disabled={loading || !billingConfigured} onClick={handleCheckout}>
                            {loading ? 'Μετάβαση στο Stripe…' : 'Επανενεργοποίηση Scanner — €79/μήνα'}
                        </button>
                    </div>
                )}

                {privateBeta && (
                    <div style={{marginTop: '1.5rem'}}>
                        <p><strong>Private Beta access ενεργό.</strong> Αυτός ο λογαριασμός έχει δωρεάν invite-only πρόσβαση και δεν χρεώνεται από Stripe.</p>
                    </div>
                )}

                {!privateBeta && scannerAccess && (
                    <div style={{marginTop: '1.5rem'}}>
                        <p>Η πρόσβαση στο Scanner είναι ενεργή. Η διαχείριση κάρτας, invoices και ακύρωσης γίνεται απευθείας στο Stripe Customer Portal.</p>
                        <button className="control-button" disabled={loading} onClick={handleManageSubscription}>
                            {loading ? 'Άνοιγμα…' : 'Διαχείριση συνδρομής'}
                        </button>
                    </div>
                )}

                <div style={{marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,.08)', color: '#929aaa', fontSize: '.82rem'}}>
                    Τα στοιχεία κάρτας δεν αποθηκεύονται στο AlgoStock. Η πληρωμή και η αποθήκευση payment method γίνονται στο ασφαλές Stripe Checkout.
                    Οι εμφανιζόμενες τιμές είναι τελικές με VAT included. Το Stripe Tax υπολογίζει το VAT που περιλαμβάνεται στην τιμή βάσει της τοποθεσίας/φορολογικής κατάστασης του πελάτη. Στο checkout συλλέγονται billing address και Tax ID όπου απαιτείται.
                </div>
            </div>
        </div>
    );
};

export default Billing;
